import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useNavigate, useLocation } from 'react-router-dom'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import OutlinedInput from '@mui/material/OutlinedInput'
import InputAdornment from '@mui/material/InputAdornment'
import IconButton from '@mui/material/IconButton'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import CircularProgress from '@mui/material/CircularProgress'
import { passwordVisibilityIconSrc } from '@/shared/lib/password-visibility'
import { useDocumentTitle } from '@/shared/hooks/useDocumentTitle'
import { useLogin } from '../api'
import { loginSchema, type LoginFormData } from '../lib'
import { PLATFORM_ADMIN_FIRST_LOGIN_PATH } from '../lib/constants'
import { ApiError } from '@/api/http/types'
import {
  isPostInviteWelcomePending,
  postInviteWelcomeQuery,
} from '@/features/dashboard/lib/post-invite-welcome'
import loginIllustration from '@/assets/login-illustration.png'
import brushfireLogo from '@/assets/brushfire-logo.png'

const REMEMBERED_EMAIL_KEY = 'brushfire_remembered_email'

const inputSx = {
  height: 50,
  bgcolor: '#ffffff',
  borderRadius: '8px',
  fontSize: '16px',
  letterSpacing: '-0.9525px',
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: '#ededed',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: '#ededed',
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: '#ededed',
    borderWidth: '1px',
  },
  '& .MuiOutlinedInput-input': {
    px: '16px',
    py: '14px',
    '&::placeholder': {
      color: '#9ca3af',
      opacity: 1,
    },
    // Neutralize browser autofill highlight (Chrome blue / Safari yellow)
    '&:-webkit-autofill': {
      WebkitBoxShadow: '0 0 0 100px #ffffff inset',
      WebkitTextFillColor: '#0f172a',
      caretColor: '#0f172a',
      transition: 'background-color 5000s ease-in-out 0s',
    },
    '&:-webkit-autofill:hover': {
      WebkitBoxShadow: '0 0 0 100px #ffffff inset',
      WebkitTextFillColor: '#0f172a',
    },
    '&:-webkit-autofill:focus': {
      WebkitBoxShadow: '0 0 0 100px #ffffff inset',
      WebkitTextFillColor: '#0f172a',
    },
  },
} as const

const errorInputSx = {
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: '#d32f2f',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: '#d32f2f',
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: '#d32f2f',
    borderWidth: '1px',
  },
} as const

const labelSx = {
  fontWeight: 500,
  fontSize: '16px',
  lineHeight: '20px',
  letterSpacing: '-0.9525px',
  color: '#0f172a',
  mb: '8px',
} as const

function getServerErrorMessage(error: unknown): string | null {
  if (error instanceof ApiError) {
    if (error.isRateLimited) return error.message
    return error.message
  }
  if (error) return 'An unexpected error occurred. Please try again.'
  return null
}

export function LoginPage() {
  useDocumentTitle('Login')
  const navigate = useNavigate()
  const location = useLocation()
  const login = useLogin()

  const [showPassword, setShowPassword] = useState(false)

  const savedEmail = localStorage.getItem(REMEMBERED_EMAIL_KEY) ?? ''
  const [rememberMe, setRememberMe] = useState(!!savedEmail)

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: savedEmail, password: '' },
  })

  const onSubmit = (data: LoginFormData) => {
    login.mutate(
      { email: data.email, password: data.password, remember_me: rememberMe },
      {
        onSuccess: async (me) => {
          if (rememberMe) {
            localStorage.setItem(REMEMBERED_EMAIL_KEY, data.email)
          } else {
            localStorage.removeItem(REMEMBERED_EMAIL_KEY)
          }

          if (me.requiresPasswordReset) {
            navigate(PLATFORM_ADMIN_FIRST_LOGIN_PATH)
            return
          }

          // Tenant host handoff is owned solely by `TenantHostRedirect` after
          // navigate(`/`). Doing handoff here raced RequireGuest's Navigate and
          // double-fired `/handoff/create`, which could bounce apex↔tenant.
          const showPostInviteWelcome = isPostInviteWelcomePending(
            new URLSearchParams(location.search),
          )
          const welcomeSearch = `?${postInviteWelcomeQuery()}`

          navigate(
            showPostInviteWelcome
              ? { pathname: '/', search: welcomeSearch }
              : '/',
          )
        },
      },
    )
  }

  const serverError = getServerErrorMessage(login.error)

  const emailReg = register('email')
  const passwordReg = register('password')

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* Left Panel — Hero */}
      <Box
        sx={{
          width: '50%',
          bgcolor: '#04183f',
          display: { xs: 'none', md: 'flex' },
          flexDirection: 'column',
          pl: '100px',
          pt: '80px',
          overflow: 'hidden',
        }}
      >
        <Box sx={{ width: 350 }}>
          <Box
            component="img"
            src={loginIllustration}
            alt=""
            sx={{ width: 280, height: 373, objectFit: 'cover', display: 'block' }}
          />
        </Box>

        <Box
          sx={{
            width: 480,
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
            flexGrow: 1,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              flexGrow: 1,
            }}
          >
            <Typography
              component="h1"
              sx={{
                fontWeight: 400,
                fontSize: '72px',
                lineHeight: '72px',
                letterSpacing: '-2.757px',
                color: '#ffffff',
              }}
            >
              Turn
              <br />
              Signals Into
              <br />
              Strategic
              <br />
              Decisions
            </Typography>
          </Box>

          <Box sx={{ pb: '80px' }}>
            <Typography
              sx={{
                fontWeight: 400,
                fontSize: '16px',
                lineHeight: '26px',
                letterSpacing: '-0.3125px',
                color: 'rgba(255, 255, 255, 0.88)',
                maxWidth: 448,
              }}
            >
              Collect real-world signals, uncover hidden patterns, and make
              confident decisions with AI-powered intelligence.
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Right Panel — Login Form */}
      <Box
        sx={{
          width: { xs: '100%', md: '50%' },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: '#ffffff',
          px: { xs: 3, md: 0 },
        }}
      >
        <Box
          sx={{
            width: 400,
            maxWidth: '100%',
            display: 'flex',
            flexDirection: 'column',
            gap: '40px',
          }}
        >
          {/* Header: Logo + Welcome + Subtitle */}
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <Box
                component="img"
                src={brushfireLogo}
                alt="Brushfire"
                sx={{ width: 237, height: 108, objectFit: 'contain' }}
              />
            </Box>

            <Typography
              component="h2"
              sx={{
                fontWeight: 600,
                fontSize: '56px',
                lineHeight: '56px',
                letterSpacing: '-3.059px',
                color: '#0f172a',
                textAlign: 'center',
                mt: '16px',
              }}
            >
              Welcome
            </Typography>

            <Typography
              sx={{
                fontWeight: 400,
                fontSize: '14px',
                lineHeight: '16px',
                letterSpacing: '-0.43px',
                color: '#4c5357',
                textAlign: 'center',
                mt: '16px',
              }}
            >
              Enter your email and password to access your account
            </Typography>
          </Box>

          {/* Form Fields */}
          <Box
            component="form"
            noValidate
            onSubmit={handleSubmit(onSubmit)}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '24px',
            }}
          >
            {/* Email */}
            <Box>
              <Typography sx={labelSx}>Email</Typography>
              <OutlinedInput
                placeholder="Enter your email"
                fullWidth
                autoComplete="email"
                error={!!errors.email}
                inputRef={emailReg.ref}
                name={emailReg.name}
                onChange={emailReg.onChange}
                onBlur={emailReg.onBlur}
                sx={{ ...inputSx, ...(errors.email ? errorInputSx : {}) }}
              />
              {errors.email && (
                <Typography sx={{ color: '#d32f2f', fontSize: '12px', mt: '4px' }}>
                  {errors.email.message}
                </Typography>
              )}
            </Box>

            {/* Password */}
            <Box>
              <Typography sx={labelSx}>Password</Typography>
              <OutlinedInput
                type={showPassword ? 'text' : 'password'}
                placeholder="Enter your password"
                fullWidth
                autoComplete="current-password"
                error={!!errors.password}
                inputRef={passwordReg.ref}
                name={passwordReg.name}
                onChange={passwordReg.onChange}
                onBlur={passwordReg.onBlur}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      edge="end"
                      aria-label="toggle password visibility"
                      tabIndex={-1}
                      onClick={() => setShowPassword((prev) => !prev)}
                      sx={{ color: '#9ca3af' }}
                    >
                      <Box
                        component="img"
                        src={passwordVisibilityIconSrc(showPassword)}
                        alt=""
                        sx={{ width: 20, height: 16 }}
                      />
                    </IconButton>
                  </InputAdornment>
                }
                sx={{ ...inputSx, ...(errors.password ? errorInputSx : {}) }}
              />
              {errors.password && (
                <Typography sx={{ color: '#d32f2f', fontSize: '12px', mt: '4px' }}>
                  {errors.password.message}
                </Typography>
              )}

              {/* Remember me / Forgot password */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mt: '12px',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <Checkbox
                    size="small"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    sx={{
                      p: 0,
                      width: 15.75,
                      height: 15.75,
                      color: '#000000',
                      '&.Mui-checked': { color: '#04183f' },
                      '& .MuiSvgIcon-root': { fontSize: 15.75 },
                    }}
                  />
                  <Typography
                    sx={{
                      fontWeight: 500,
                      fontSize: '12px',
                      lineHeight: '20px',
                      letterSpacing: '-0.6px',
                      color: '#3d3d3d',
                    }}
                  >
                    Remember me
                  </Typography>
                </Box>

                <Typography
                  component="a"
                  href="#"
                  onClick={(e: React.MouseEvent) => {
                    e.preventDefault()
                    navigate('/forgot-password')
                  }}
                  sx={{
                    fontWeight: 500,
                    fontSize: '12px',
                    lineHeight: '20px',
                    letterSpacing: '-0.6px',
                    color: '#3d3d3d',
                    textDecoration: 'none',
                    textAlign: 'center',
                    cursor: 'pointer',
                    '&:hover': { textDecoration: 'underline' },
                  }}
                >
                  Forgot password?
                </Typography>
              </Box>
            </Box>

            {/* Business logic addition — not in Figma */}
            {serverError && (
              <Typography
                sx={{
                  fontWeight: 400,
                  fontSize: '13px',
                  lineHeight: '20px',
                  letterSpacing: '-0.1504px',
                  color: '#ef4444',
                }}
              >
                {serverError}
              </Typography>
            )}

            {/* Sign In Button */}
            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={login.isPending}
              sx={{
                height: 50,
                bgcolor: '#2196F3',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: 500,
                letterSpacing: '-0.3px',
                color: '#ffffff',
                textTransform: 'none',
                boxShadow: 'none',
                '&:hover': {
                  bgcolor: '#1E88E5',
                  boxShadow: 'none',
                },
              }}
            >
              {login.isPending ? (
                <CircularProgress size={24} sx={{ color: '#ffffff' }} />
              ) : (
                'Sign in'
              )}
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}
